import { useCallback } from 'react'
import { useAuth } from './auth'

export const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:5000'
const BASE = API_BASE

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback

  const payload = err as {
    message?: unknown
    title?: unknown
    error?: {
      message?: unknown
    }
  }

  if (typeof payload.error?.message === 'string' && payload.error.message.trim()) return payload.error.message
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message
  if (typeof payload.title === 'string' && payload.title.trim()) return payload.title
  return fallback
}

export function useApi() {
  const { getToken, logout } = useAuth()

  const request = useCallback(async <T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> => {
    const token = await getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    const res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      if (res.status === 423) await logout().catch(() => {})
      throw Object.assign(
        new Error(getApiErrorMessage(err, `HTTP ${res.status}`)),
        { status: res.status },
      )
    }

    if (res.status === 204) return undefined as T
    return res.json() as Promise<T>
  }, [getToken, logout])

  const postFile = useCallback(async <T>(path: string, formData: FormData): Promise<T> => {
    const token = await getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    // No Content-Type — browser sets it with the multipart boundary

    const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: formData })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      if (res.status === 423) await logout().catch(() => {})
      throw Object.assign(
        new Error(getApiErrorMessage(err, `HTTP ${res.status}`)),
        { status: res.status },
      )
    }
    return res.json() as Promise<T>
  }, [getToken, logout])

  const download = useCallback(async (path: string, filename: string) => {
    const token = await getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${BASE}${path}`, { headers })
    if (!res.ok) {
      if (res.status === 423) await logout().catch(() => {})
      throw new Error(`HTTP ${res.status}`)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; a.click()
    URL.revokeObjectURL(url)
  }, [getToken, logout])

  return {
    get:      <T>(path: string)                    => request<T>('GET',    path),
    post:     <T>(path: string, body?: unknown)    => request<T>('POST',   path, body),
    put:      <T>(path: string, body?: unknown)    => request<T>('PUT',    path, body),
    patch:    <T>(path: string, body?: unknown)    => request<T>('PATCH',  path, body),
    delete:   <T>(path: string)                    => request<T>('DELETE', path),
    postFile,
    download,
  }
}

/** Convert API enum string to display label: "InProgress" → "IN PROGRESS" */
export function apiStatus(s: string): string {
  return s.replace(/([A-Z])/g, ' $1').trim().toUpperCase()
}
